/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param num - The number to clamp.
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns The clamped number.
 */
const clamp = (num: number, min: number, max: number): number => {
    return Math.min(Math.max(num, min), max);
};

/**
 * Configuration for the color gradient.
 * Defines thresholds and corresponding hue values.
 * For meaningful interpolation, it's recommended that:
 * `0 <= lowThreshold < midThreshold < 100`.
 * Hue values are typically between 0 and 360.
 */
export interface GradientConfig {
    lowThreshold?: number;
    midThreshold?: number;
    lowHue?: number;
    midHue?: number;
    highHue?: number;
}

const defaultGradientConfig: Required<GradientConfig> = {
    lowThreshold: 0,
    midThreshold: 50,
    lowHue: 0, // Red
    midHue: 35, // Orange
    highHue: 120, // Green
};

/**
 * Calculates an HSL color string based on a percentage value and an optional gradient configuration.
 * The color transitions through hues defined by the configuration.
 *
 * @param percentage - The percentage value (0-100).
 * @param customConfig - Optional configuration for the color gradient.
 * @returns An HSL color string (e.g., "hsl(120, 100%, 35%)").
 */
export const getPercentageColor = (
    percentage: number,
    customConfig?: GradientConfig,
): string => {
    const config = { ...defaultGradientConfig, ...customConfig };
    const { lowThreshold, midThreshold, lowHue, midHue, highHue } = config;

    // Clamp the percentage between 0 and 100
    const clampedPercentage = clamp(percentage, 0, 100);

    let hue;

    if (clampedPercentage <= lowThreshold) {
        hue = lowHue;
    } else if (clampedPercentage <= midThreshold) {
        // This branch implies lowThreshold < clampedPercentage <= midThreshold.
        // Interpolation occurs from lowHue at lowThreshold to midHue at midThreshold.
        // If lowThreshold == midThreshold, this branch is effectively skipped.
        const denominator = midThreshold - lowThreshold;
        // Ensure denominator is not zero to prevent division by zero,
        // though the logic of if/else if should prevent reaching here if denominator is zero
        // (i.e. if lowThreshold == midThreshold, the first `if` or the `else` block after this one is chosen).
        if (denominator > 0) {
            hue =
                lowHue +
                ((clampedPercentage - lowThreshold) / denominator) *
                    (midHue - lowHue);
        } else {
            // Fallback if lowThreshold >= midThreshold (e.g. they are equal).
            // If clampedPercentage is at this point (midThreshold), use midHue.
            hue = midHue;
        }
    } else {
        // This branch implies midThreshold < clampedPercentage <= 100.
        // Interpolation occurs from midHue at midThreshold to highHue at 100.
        // If midThreshold == 100, this branch is effectively skipped.
        const denominator = 100 - midThreshold;
        // Ensure denominator is not zero.
        if (denominator > 0) {
            hue =
                midHue +
                ((clampedPercentage - midThreshold) / denominator) *
                    (highHue - midHue);
        } else {
            // Fallback if midThreshold >= 100 (e.g. they are equal).
            // If clampedPercentage is at this point (100), use highHue.
            hue = highHue;
        }
    }

    // Use a slightly darker green/red for better readability
    const lightness = 35;
    const saturation = 100;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};
